import { useQuery } from '@tanstack/react-query';
import { useApolloClient } from '@apollo/client/react';
import { GET_CONTACTS } from '../../lib/graphql/queries/contacts';
import { useOrganizationStore } from '../../stores/useOrganizationStore';

export const PAGE_SIZE = 25;

export interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  tags: string[];
  optIn: boolean;
  call_tester?: boolean;
  opt_in?: boolean;
  [key: string]: unknown;
}

export interface QueryResult {
  contacts: Contact[];
  cursor: number | null;
  totalCount: number;
}

interface GQLContactsData {
  contacts: {
    contacts: Array<{
      contactId: string;
      firstName: string;
      lastName: string;
      phone: string;
      tags: string[];
      optIn: boolean;
    }>;
    total: number;
    hasMore: boolean;
  };
}

function mapGQLToContact(c: GQLContactsData['contacts']['contacts'][0]): Contact {
  return {
    id: c.contactId,
    firstName: c.firstName ?? '',
    lastName: c.lastName ?? '',
    phoneNumber: c.phone ?? '',
    tags: c.tags ?? [],
    optIn: c.optIn ?? false,
    opt_in: c.optIn ?? false,
  };
}

export const useContactsByTags = (
  tags: string[],
  _flowId?: string,
  shouldFetch: boolean = false,
  page: number = 0,
  pageSize: number = PAGE_SIZE
) => {
  const { organization } = useOrganizationStore();
  const apolloClient = useApolloClient();

  const { data, isLoading, isFetching } = useQuery<QueryResult, Error>({
    queryKey: ['contacts', 'byTags', organization?.id, tags, page, pageSize],
    queryFn: async () => {
      if (!organization?.id || !tags.length) return { contacts: [], cursor: null, totalCount: 0 };
      const offset = page * pageSize;

      const { data } = await apolloClient.query<GQLContactsData>({
        query: GET_CONTACTS,
        variables: {
          organizationId: organization.id,
          tags,
          matchAllTags: true,
          limit: pageSize,
          offset,
          sortOrder: 'ASC',
          optIn: true,
        },
        fetchPolicy: 'network-only',
      });

      const contacts = (data?.contacts?.contacts ?? []).map(mapGQLToContact);

      return {
        contacts,
        cursor: null,
        totalCount: data?.contacts?.total ?? 0,
      };
    },
    enabled: !!organization?.id && tags.length > 0 && shouldFetch,
  });

  return {
    contacts: data?.contacts ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    isFetching,
  };
};
